import { useState, useCallback, useEffect } from 'react';
import { CategoryI, SubCategoryI } from '@/types/cyclone/models';

// Onboarding data structure for localStorage
export interface OnboardingState {
  step: number;
  completedSteps: number[];

  // Categories data
  fatherCategory: CategoryI | null;
  childCategories: SubCategoryI[];

  // Location data
  country: string | null;
  state: string | null;
  timezone: string | null;

  // Site data
  storefrontName: string;
  profession: string;
  username: string;
  privacyPolicies: boolean;

  // Phone data
  phone: string;

  // Metadata
  lastSavedAt: number;
  version: string; // For future migrations
}

const STORAGE_KEY = 'agora_onboarding_state';
const STORAGE_VERSION = '1.0.0';
const MAX_STORAGE_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days

// Initial state
const getInitialState = (): OnboardingState => ({
  step: 1,
  completedSteps: [],
  fatherCategory: null,
  childCategories: [],
  country: null,
  state: null,
  timezone: null,
  storefrontName: '',
  profession: '',
  username: '',
  privacyPolicies: false,
  phone: '',
  lastSavedAt: Date.now(),
  version: STORAGE_VERSION
});

export interface UseOnboardingLocalStorageReturn {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  markStepCompleted: (step: number) => void;
  isStepCompleted: (step: number) => boolean;
  clearState: () => void;
  hasStoredData: boolean;
  isDataStale: boolean;
}

export const useOnboardingLocalStorage = (): UseOnboardingLocalStorageReturn => {
  const [state, setState] = useState<OnboardingState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: OnboardingState = JSON.parse(stored);

        // Check if data is too old
        const age = Date.now() - parsed.lastSavedAt;
        if (age > MAX_STORAGE_AGE) {
          localStorage.removeItem(STORAGE_KEY);
          return getInitialState();
        }

        // Version check for future migrations
        if (parsed.version !== STORAGE_VERSION) {
          // Handle version migration here if needed
          return { ...parsed, version: STORAGE_VERSION };
        }

        return parsed;
      }
      return getInitialState();
    } catch (error) {
      // Failed to load onboarding state from localStorage
      localStorage.removeItem(STORAGE_KEY);
      return getInitialState();
    }
  });

  const [hasStoredData, setHasStoredData] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return !!stored;
    } catch {
      return false;
    }
  });

  const [isDataStale, setIsDataStale] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: OnboardingState = JSON.parse(stored);
        const age = Date.now() - parsed.lastSavedAt;
        return age > MAX_STORAGE_AGE;
      }
      return false;
    } catch {
      return false;
    }
  });

  // Debounced save to localStorage
  const saveToStorage = useCallback((newState: OnboardingState) => {
    try {
      const stateWithTimestamp = {
        ...newState,
        lastSavedAt: Date.now(),
        version: STORAGE_VERSION
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithTimestamp));
    } catch (error) {
      // Failed to save onboarding state to localStorage
      // Handle storage quota exceeded
      if (error instanceof DOMException && error.code === 22) {
        // Try to clear old data and retry
        try {
          localStorage.removeItem(STORAGE_KEY);
          const retryState = {
            ...newState,
            lastSavedAt: Date.now(),
            version: STORAGE_VERSION
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(retryState));
        } catch (retryError) {
          // Failed to save after clearing storage
        }
      }
    }
  }, []);

  const updateState = useCallback(
    (updates: Partial<OnboardingState>) => {
      setState((prevState) => {
        const newState = { ...prevState, ...updates };
        saveToStorage(newState);
        return newState;
      });
    },
    [saveToStorage]
  );

  const markStepCompleted = useCallback(
    (step: number) => {
      updateState({
        completedSteps: [...new Set([...state.completedSteps, step])]
      });
    },
    [state.completedSteps, updateState]
  );

  const isStepCompleted = useCallback(
    (step: number) => {
      return state.completedSteps.includes(step);
    },
    [state.completedSteps]
  );

  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setState(getInitialState());
      setHasStoredData(false);
      setIsDataStale(false);
    } catch (error) {
      // Failed to clear onboarding state
    }
  }, []);

  // Auto-save state changes
  useEffect(() => {
    saveToStorage(state);
  }, [state, saveToStorage]);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const newState = JSON.parse(e.newValue);
          setState(newState);
        } catch (error) {
          // Failed to sync onboarding state across tabs
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    state,
    updateState,
    markStepCompleted,
    isStepCompleted,
    clearState,
    hasStoredData,
    isDataStale
  };
};
